import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

import {
  INNER_BOTTOM_SPACE_DESK,
  colorsPalette,
  MIDDLE_MARGIN,
  SMALL_MARGIN,
  LARGE_MARGIN,
} from '../../theme';
import { Background } from '../../types';

const INNER_BOTTOM_SPACE_MOB = 24;

const useStyles = (background, startFromImage) =>
  makeStyles((theme: Theme) =>
    createStyles({
      wrapper: {
        paddingTop: MIDDLE_MARGIN,
        paddingBottom: MIDDLE_MARGIN,
        background: theme.palette.secondary.dark,
        width: '100%',
        [theme.breakpoints.up('sm')]: {
          paddingTop: LARGE_MARGIN,
          paddingBottom: LARGE_MARGIN,
          background:
            background === 'Dark' ? theme.palette.secondary.dark : theme.palette.secondary.light,
        },

        '&:first-child': {
          marginTop: 76,
        },
      },
      wrapperWithBgImage: {
        paddingTop: 0,
        paddingBottom: 0,
        [theme.breakpoints.up('sm')]: {
          paddingTop: 0,
          paddingBottom: 0,
        },
        '& $content': {
          [theme.breakpoints.up('lg')]: {
            justifyContent: 'flex-end',
          },
        },
        '& $singleImageWrap': {
          overflow: 'hidden',
          width: 'calc(100% + 32px)',
          marginLeft: -16,
          marginRight: -16,
          maxHeight: '100%',
          height: '100%',
          [theme.breakpoints.up('lg')]: {
            top: 0,
            ...(startFromImage ? { left: 0 } : { right: 0 }),
            position: 'absolute',
            width: 'calc(50% + 16px)',
            ...(startFromImage ? { marginRight: 0 } : { marginLeft: 0 }),
            '& $imagePreloaderWrap': {
              display: 'flex',
              justifyContent: startFromImage ? 'flex-start' : 'flex-end',
              maxWidth: 'unset',
              maxHeight: 'unset',
              width: '100%',
              height: '100%',
            },
            '& $imagePreloaderWrap img': {
              maxWidth: 'unset',
              maxHeight: 'unset',
            },
          },
          '& img': {
            width: '100%',
            maxHeight: 400,
            borderRadius: 0,
            objectFit: 'cover',
            [theme.breakpoints.up('lg')]: {
              width: '100%',
              maxHeight: 'unset',
            },
          },
        },
        '& $textContainer': {
          paddingBottom: MIDDLE_MARGIN,
          [theme.breakpoints.up('sm')]: {
            paddingBottom: LARGE_MARGIN,
          },
          [theme.breakpoints.up('lg')]: {
            paddingTop: 120,
            paddingBottom: 120,
          },
        },
        '& $itemHeading': {
          display: 'block',
        },
      },
      container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        color: colorsPalette.white,
        paddingLeft: 16,
        paddingRight: 16,
        width: '100%',
      },
      wideContainer: {
        [theme.breakpoints.up('xl')]: {
          maxWidth: 1600,
        },
      },
      contentWrapper: {
        width: '100%',
      },
      content: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',

        [theme.breakpoints.up('lg')]: {
          flexDirection: 'row',
          alignItems: 'center',
          position: 'relative',
          ...(!startFromImage && { flexDirection: 'row-reverse' }),
        },
      },
      odd: {},
      heading: {
        fontSize: 18,
        lineHeight: '22px',
        marginBottom: SMALL_MARGIN,
        [theme.breakpoints.up('lg')]: {
          display: 'none',
          marginBottom: INNER_BOTTOM_SPACE_DESK,
        },
      },
      imgGradientWrapper: {
        display: 'inline-flex',
        position: 'relative',
        maxWidth: '100%',
        width: '100%',
        '&::before': {
          content: '""',
          display: 'block',
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          background: `linear-gradient(180deg, rgba(35, 32, 46, 0) 37.59%, ${colorsPalette.blueDark} 95.39%)`,
        },
        [theme.breakpoints.up('sm')]: {
          '&::before': {
            background: `linear-gradient(180deg, rgba(35, 32, 46, 0) 37.59%, ${
              background === Background.Dark ? colorsPalette.blueDark : colorsPalette.blackSecondary
            } 95.39%)`,
          },
        },
        [theme.breakpoints.up('lg')]: {
          width: 'unset',
          '&::before': {
            background: `linear-gradient(${
              startFromImage ? '90deg' : '-90deg'
            }, rgba(35, 32, 46, 0) 37.59%, ${
              background === Background.Dark ? colorsPalette.blueDark : colorsPalette.blackSecondary
            } 95.39%)`,
          },
        },
      },
      singleImageWrap: {
        width: 343,
        marginBottom: SMALL_MARGIN,
        [theme.breakpoints.up('lg')]: {
          display: 'flex',
          width: '50%',
          marginBottom: 0,
        },
        '& img': {
          width: '100%',
          objectFit: 'contain',
          maxHeight: '100%',
          borderRadius: 5,
        },
        '& $imagePreloaderWrap': {
          ...(startFromImage
            ? {
                marginLeft: 'auto',
              }
            : {
                marginRight: 'auto',
                marginLeft: 0,
              }),
          [theme.breakpoints.up('lg')]: {
            maxHeight: 470,
            maxWidth: 900,
            '& img': {
              maxWidth: 900,
              maxHeight: 470,
            },
          },
        },
      },
      firstImageWrap: {
        height: 200,
        marginBottom: INNER_BOTTOM_SPACE_MOB,
        '& $imagePreloaderWrap': {
          width: '100%',
          height: '100%',
        },
        '& img': {
          maxWidth: '100%',
          maxHeight: '100%',
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
          borderRadius: 5,
        },

        [theme.breakpoints.up('lg')]: {
          marginBottom: 40,
          width: '50%',
          height: 359,
          '& img': {
            objectFit: 'cover',
          },
        },
      },
      secondImageWrap: {
        height: 200,
        marginBottom: INNER_BOTTOM_SPACE_MOB,
        '& $imagePreloaderWrap': {
          width: '100%',
          height: '100%',
        },
        '& img': {
          maxWidth: '100%',
          maxHeight: '100%',
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
          borderRadius: 5,
        },

        [theme.breakpoints.up('lg')]: {
          position: 'absolute',
          bottom: 0,
          ...(startFromImage ? { left: '20%' } : { right: 0 }),
          width: '36%',
          height: 253,
          marginBottom: 0,

          '& img': {
            objectFit: 'cover',
          },
        },
      },
      imagePreloaderWrap: {},
      textContainer: {
        [theme.breakpoints.up('lg')]: {
          width: '50%',
        },
      },
      textWrapper: {
        marginBottom: INNER_BOTTOM_SPACE_MOB,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: '0 auto',
        '&:last-child': {
          marginBottom: 0,
        },
        '& p': {
          width: '100%',
        },
        [theme.breakpoints.up('sm')]: {
          width: '50%',
          minWidth: 343,
        },

        [theme.breakpoints.up('lg')]: {
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'flex-start',
          marginBottom: INNER_BOTTOM_SPACE_DESK,
          ...(startFromImage
            ? {
                paddingLeft: 120,
                paddingRight: 0,
                marginLeft: 0,
                marginRight: 'auto',
              }
            : {
                paddingLeft: 0,
                paddingRight: 120,
                marginLeft: 'auto',
                marginRight: 0,
              }),
        },
        [theme.breakpoints.up('xl')]: {
          maxWidth: 586,
        },
      },
      itemHeading: {
        marginBottom: SMALL_MARGIN,
        fontSize: 24,
        lineHeight: '28px',
        textAlign: 'center',
        display: 'none',
        width: '100%',
        [theme.breakpoints.up('lg')]: {
          display: 'block',
          fontSize: 28,
          lineHeight: '34px',
          textAlign: 'left',
        },
      },
      itemText: {
        marginBottom: MIDDLE_MARGIN,
        '&:last-child': {
          marginBottom: 0,
        },
        '& h1,& h2,& h3,& h4,& h5,& h6,& p,& ul,& ol': {
          marginBottom: SMALL_MARGIN,
          marginTop: 0,
          '&:last-child': {
            marginBottom: 0,
          },
        },
        '& h1,& h2,& h3,& h4,& h5,& h6,& p': {
          textAlign: 'center',
          [theme.breakpoints.up('lg')]: {
            textAlign: 'left',
          },
        },
        '&, & p, li': {
          fontSize: 16,
          lineHeight: 1.5,
          color: colorsPalette.gray,
          [theme.breakpoints.up('lg')]: {
            fontSize: 18,
          },
        },
        '& ul, & ol': {
          paddingLeft: 20,
          '& li': {
            '&::marker': {
              color: colorsPalette.turquoiseLight,
            },
          },
        },
        '& a': {
          color: colorsPalette.white,
          fontWeight: 'bold',
          '&:hover': {
            color: colorsPalette.blue,
          },
        },
      },
      btn: {
        width: '100%',
        [theme.breakpoints.up('sm')]: {
          width: 'unset',
        },
      },
      leftAlign: {
        alignSelf: 'flex-start',
      },
    }),
  );
export default useStyles;
